import { useEffect, useState } from 'react';

/**
 * Hash router.
 *
 * WagePro ships as a static bundle that a Capacitor shell also loads from the
 * filesystem, so hash routing keeps deep links working with no server rewrite
 * rules. Parsing is a pure function over the hash string: every screen in the
 * app is reachable by URL, and the routing table is unit-testable without a DOM.
 */

export type NavModule =
  | 'dashboard'
  | 'employees'
  | 'expenses'
  | 'leave'
  | 'payroll'
  | 'timesheets'
  | 'update_requests'
  | 'settings'
  | 'my_pay'
  | 'my_leave'
  | 'my_profile';

export const EMPLOYEE_PROFILE_TABS = [
  'job',
  'payroll',
  'bank',
  'access',
] as const;
export type EmployeeProfileTab = (typeof EMPLOYEE_PROFILE_TABS)[number];

export const EXPENSE_TABS = [
  'pending',
  'approved',
  'paid',
  'rejected',
] as const;
export type ExpenseTab = (typeof EXPENSE_TABS)[number];

export const LEAVE_TABS = [
  'pending',
  'approved',
  'rejected',
  'cancelled',
  'balances',
] as const;
export type LeaveTab = (typeof LEAVE_TABS)[number];

export const PAYROLL_TABS = [
  'run',
  'history',
  'reports',
  'data-centre',
  'policies',
] as const;
export type PayrollTab = (typeof PAYROLL_TABS)[number];

export const DATA_CENTRE_TABS = [
  'allowances',
  'deductions',
  'tax',
  'bank',
  'loans',
] as const;
export type DataCentreTab = (typeof DATA_CENTRE_TABS)[number];

export const SETTINGS_TABS = [
  'organisation',
  'policies',
  'roles',
  'operations',
  'payroll',
  'leave_types',
  'import',
  'security',
] as const;
export type SettingsTab = (typeof SETTINGS_TABS)[number];

export type Route =
  | { module: 'dashboard' }
  | { module: 'employees'; view: 'directory' }
  | {
      module: 'employees';
      view: 'profile';
      employee_id: string | null;
      tab: EmployeeProfileTab;
    }
  | { module: 'expenses'; tab: ExpenseTab }
  | { module: 'leave'; tab: LeaveTab }
  | {
      module: 'payroll';
      tab: 'run' | 'history' | 'reports' | 'policies';
    }
  | { module: 'payroll'; tab: 'data-centre'; data_centre_tab: DataCentreTab }
  | { module: 'timesheets' }
  | { module: 'update_requests' }
  | { module: 'settings'; tab: SettingsTab }
  | { module: 'my_pay' }
  | { module: 'my_leave' }
  | { module: 'my_profile' };

export const DEFAULT_ROUTE: Route = { module: 'dashboard' };

/**
 * The data-import wizard's one home is Settings -> Data import. It used to
 * have two entry points (`#/employees/import` and `#/payroll/import`); those
 * old hashes still resolve here so a bookmark lands on the wizard instead of
 * falling through to a default screen.
 */
const LEGACY_IMPORT_ROUTE: Route = { module: 'settings', tab: 'import' };

/**
 * Stands in for the employee id in a profile URL when the caller has not
 * picked one yet — the nav's "Bank information" shortcut, for instance, opens
 * whichever employee the profile screen resolves as current. Reserving a
 * segment keeps the path shape stable instead of collapsing to `profile//tab`.
 */
export const CURRENT_EMPLOYEE_SEGMENT = 'current';

function pick<T extends string>(
  allowed: readonly T[],
  candidate: string | undefined,
  fallback: T,
): T {
  return allowed.includes(candidate as T) ? (candidate as T) : fallback;
}

/**
 * Parse a location hash (with or without the leading `#`) into a Route.
 * An unrecognised path falls back to the dashboard rather than rendering
 * nothing — a stale bookmark should never produce a blank screen.
 */
export function parse_route(hash: string): Route {
  const path = hash.replace(/^#/, '').replace(/^\/+/, '');
  const parts = path.split('/').filter((part) => part.length > 0);
  // `.at()` is typed `string | undefined`, so a short path stays type-safe
  // instead of silently producing an `undefined` typed as `string`.
  const head = parts.at(0);
  /** Nth segment after the module, or undefined when the path is shorter. */
  const rest = (index: number): string | undefined => parts.at(index + 1);

  switch (head) {
    case undefined:
    case 'dashboard':
      return { module: 'dashboard' };

    case 'employees':
      if (rest(0) === 'import') {
        return LEGACY_IMPORT_ROUTE;
      }
      if (rest(0) === 'profile') {
        const id_segment = rest(1);
        return {
          module: 'employees',
          view: 'profile',
          employee_id:
            id_segment === undefined || id_segment === CURRENT_EMPLOYEE_SEGMENT
              ? null
              : id_segment,
          tab: pick(EMPLOYEE_PROFILE_TABS, rest(2), 'job'),
        };
      }
      return { module: 'employees', view: 'directory' };

    case 'expenses':
      return {
        module: 'expenses',
        tab: pick(EXPENSE_TABS, rest(0), 'pending'),
      };

    case 'leave':
      return {
        module: 'leave',
        tab: pick(LEAVE_TABS, rest(0), 'pending'),
      };

    case 'payroll': {
      if (rest(0) === 'import') return LEGACY_IMPORT_ROUTE;
      const tab = pick(PAYROLL_TABS, rest(0), 'run');
      if (tab === 'data-centre') {
        return {
          module: 'payroll',
          tab,
          data_centre_tab: pick(DATA_CENTRE_TABS, rest(1), 'allowances'),
        };
      }
      return { module: 'payroll', tab };
    }

    case 'timesheets':
      return { module: 'timesheets' };

    case 'update-requests':
      return { module: 'update_requests' };

    case 'settings':
      return { module: 'settings', tab: pick(SETTINGS_TABS, rest(0), 'roles') };

    case 'my-pay':
      return { module: 'my_pay' };

    case 'my-leave':
      return { module: 'my_leave' };

    case 'my-profile':
      return { module: 'my_profile' };

    default:
      return DEFAULT_ROUTE;
  }
}

/** Build the canonical hash path for a route — the inverse of `parse_route`. */
export function route_to_path(route: Route): string {
  switch (route.module) {
    case 'dashboard':
      return '#/';
    case 'employees':
      if (route.view === 'directory') return '#/employees/directory';
      return `#/employees/profile/${route.employee_id ?? CURRENT_EMPLOYEE_SEGMENT}/${route.tab}`;
    case 'expenses':
      return `#/expenses/${route.tab}`;
    case 'leave':
      return `#/leave/${route.tab}`;
    case 'payroll':
      return route.tab === 'data-centre'
        ? `#/payroll/data-centre/${route.data_centre_tab}`
        : `#/payroll/${route.tab}`;
    case 'timesheets':
      return '#/timesheets';
    case 'update_requests':
      return '#/update-requests';
    case 'settings':
      return `#/settings/${route.tab}`;
    case 'my_pay':
      return '#/my-pay';
    case 'my_leave':
      return '#/my-leave';
    case 'my_profile':
      return '#/my-profile';
  }
}

/** Navigate to a route, adding a history entry. */
export function navigate(route: Route): void {
  window.location.hash = route_to_path(route).replace(/^#/, '');
}

/** Subscribe to the active route. */
export function useRoute(): Route {
  const [route, set_route] = useState<Route>(() =>
    parse_route(window.location.hash),
  );

  useEffect(() => {
    const on_change = (): void => {
      set_route(parse_route(window.location.hash));
    };
    window.addEventListener('hashchange', on_change);
    return () => {
      window.removeEventListener('hashchange', on_change);
    };
  }, []);

  return route;
}
